import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIALS } from "@/constants/testIds";

const items = [
  {
    id: "fmcg",
    quote:
      "FreightXchange have become an extension of our supply chain team. One phone call and a truck is on its way — even on the lanes nobody else wants to run.",
    name: "Logistics Manager",
    company: "FMCG Distributor, Gauteng",
    role: "Outbound",
    avatar:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  },
  {
    id: "mining",
    quote:
      "On heavy-haul and abnormal moves they get the permits, the escorts and the right rig sorted without us having to babysit anything. Reliable, every single time.",
    name: "Brendan Mokoena",
    company: "Mining Services, Mpumalanga",
    role: "Procurement Lead",
    avatar:
      "https://images.pexels.com/photos/17582358/pexels-photo-17582358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400",
  },
  {
    id: "cold",
    quote:
      "Cold-chain work used to keep me awake at night. With FreightXchange, my fridges arrive on temp, on time — and one invoice covers the lot. Total game changer.",
    name: "Sandra Naidoo",
    company: "Cold Chain Specialists, KZN",
    role: "Operations Director",
    avatar:
      "https://images.pexels.com/photos/27086922/pexels-photo-27086922.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400",
  },
];

export default function Testimonials() {
  const [i, setI] = useState(0);
  const next = () => setI((p) => (p + 1) % items.length);
  const prev = () => setI((p) => (p - 1 + items.length) % items.length);
  const t = items[i];

  return (
    <section
      id="testimonials"
      data-testid={TESTIMONIALS.section}
      className="relative py-24 md:py-32 bg-[#F8FAFC] border-y border-[#E2E8F0]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs md:text-sm uppercase tracking-[0.24em] font-bold text-[#0070C0]">
              Customer stories
            </div>
            <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
              Trusted by the teams that move South Africa.
            </h2>
            <p className="mt-5 text-lg text-[#1F2937]/75 leading-relaxed">
              From mining and manufacturing to cold-chain and FMCG, our
              shippers stay with us because the relationships work — load
              after load.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <button
                onClick={prev}
                data-testid={TESTIMONIALS.prev}
                className="h-12 w-12 rounded-full border border-[#E2E8F0] bg-white hover:bg-[#F1F5F9] flex items-center justify-center text-[#002050] transition-colors"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={next}
                data-testid={TESTIMONIALS.next}
                className="h-12 w-12 rounded-full bg-[#002050] hover:bg-[#0070C0] flex items-center justify-center text-white transition-colors"
                aria-label="Next testimonial"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="ml-3 text-sm text-[#1F2937]/60 font-medium">
                {String(i + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.4 }}
                data-testid={TESTIMONIALS.card(t.id)}
                className="relative rounded-3xl bg-white border border-[#E2E8F0] p-8 md:p-12 shadow-[0_30px_80px_-30px_rgba(0,32,80,0.25)]"
              >
                <Quote className="absolute -top-5 left-8 h-10 w-10 text-[#0070C0] bg-white p-2 rounded-xl border border-[#E2E8F0]" />
                <div className="flex gap-1 text-[#00AEEF]">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className="h-5 w-5 fill-[#00AEEF]" />
                  ))}
                </div>
                <p className="mt-5 font-display text-2xl md:text-3xl leading-snug text-[#002050] font-medium tracking-tight">
                  “{t.quote}”
                </p>
                <div className="mt-8 flex items-center gap-4">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="h-14 w-14 rounded-full object-cover border-2 border-white shadow-md"
                  />
                  <div>
                    <div className="font-bold text-[#002050]">{t.name}</div>
                    <div className="text-sm text-[#1F2937]/70">
                      {t.role} · {t.company}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
